import React, { useEffect, useMemo, useState } from "react";
import {
	Box,
	Card,
	CardContent,
	CircularProgress,
	Divider,
	Grid,
	MenuItem,
	Paper,
	Stack,
	TextField,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
	useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	ResponsiveContainer,
	LineChart,
	Line,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip,
} from "recharts";
import dayjs from "dayjs";
import ListSubheader from "@mui/material/ListSubheader";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BACKEND_TOKEN = process.env.REACT_APP_BACKEND_TOKEN;

const numberFormatter = (value, options = {}) => {
	if (value == null || Number.isNaN(value)) return "—";
	return new Intl.NumberFormat(undefined, options).format(value);
};

const formatPercent = (value, digits = 1) => {
	if (value == null || Number.isNaN(value)) return "—";
	return `${numberFormatter(value, {
		maximumFractionDigits: digits,
		minimumFractionDigits: Math.min(1, digits),
	})}%`;
};

const formatHours = (value, digits = 1) => {
	if (value == null || Number.isNaN(value)) return "—";
	return `${numberFormatter(value, {
		maximumFractionDigits: digits,
		minimumFractionDigits: 0,
	})} hr`;
};

const formatSeconds = (value, digits = 1) => {
	if (value == null || Number.isNaN(value)) return "—";
	return `${numberFormatter(value, {
		maximumFractionDigits: digits,
		minimumFractionDigits: 0,
	})} s`;
};

const formatPeriod = (period) => {
	if (!period?.start || !period?.end) return "—";
	const start = dayjs(period.start);
	const end = dayjs(period.end);
	if (!start.isValid() || !end.isValid()) return "—";
	return `${start.format("DD MMM YYYY")} – ${end.format("DD MMM YYYY")}`;
};

const formatTimestamp = (value) => {
	if (!value) return "—";
	const ts = dayjs(value);
	if (!ts.isValid()) return "—";
	return ts.format("DD MMM YYYY, HH:mm");
};

const toActiveLabel = (value) => {
	if (value == null) return "—";
	return Number(value) > 0 ? "Active" : "Inactive";
};

const SECTION_DEFS = [
	{
		key: "scores",
		title: "Scores",
		span: 12,
		metrics: [
			{ field: "composite_vehicle_score", label: "Composite", formatter: (v) => formatPercent(v, 1) },
			{ field: "performance_score", label: "Performance", formatter: (v) => formatPercent(v, 1) },
			{ field: "energy_score", label: "Energy", formatter: (v) => formatPercent(v, 1) },
			{ field: "health_score", label: "Health", formatter: (v) => formatPercent(v, 1) },
		],
	},
	{
		key: "utilization",
		title: "Utilization",
		span: 12,
		metrics: [
			{ field: "distance_km", label: "Distance", formatter: (v) => `${numberFormatter(v, { maximumFractionDigits: 1 })} km` },
			{ field: "trips_count", label: "Trips", formatter: (v) => numberFormatter(v, { maximumFractionDigits: 0 }) },
			{ field: "avg_trip_length_km", label: "Avg Trip Length", formatter: (v) => `${numberFormatter(v, { maximumFractionDigits: 1 })} km` },
			{ field: "avg_daily_km", label: "Avg Daily Distance", formatter: (v) => `${numberFormatter(v, { maximumFractionDigits: 1 })} km` },
			{ field: "driving_hours", label: "Driving Time", formatter: (v) => formatHours(v, 1) },
			{ field: "idle_hours", label: "Idle Time", formatter: (v) => formatHours(v, 1) },
			{ field: "active_days", label: "Active Days", formatter: (v) => numberFormatter(v, { maximumFractionDigits: 0 }) },
			{ field: "active_flag", label: "Activity", formatter: toActiveLabel },
		],
	},
	{
		key: "energy",
		title: "Energy",
		span: 6,
		metrics: [
			{ field: "kwh_used", label: "Energy Used", formatter: (v) => `${numberFormatter(v, { maximumFractionDigits: 1 })} kWh` },
			{ field: "kwh_charged", label: "Energy Charged", formatter: (v) => `${numberFormatter(v, { maximumFractionDigits: 1 })} kWh` },
			{ field: "charging_hours", label: "Charging Hours", formatter: (v) => formatHours(v, 1) },
			{ field: "km_per_kwh", label: "Efficiency", formatter: (v) => `${numberFormatter(v, { maximumFractionDigits: 2 })} km/kWh` },
			{ field: "charging_efficiency_pct", label: "Charging Efficiency", formatter: (v) => formatPercent(v, 1) },
			{ field: "estimated_energy_loss_pct", label: "Energy Loss", formatter: (v) => formatPercent(v, 1) },
			{ field: "battery_soh_pct", label: "Battery SOH", formatter: (v) => formatPercent(v, 0) },
		],
	},
	{
		key: "health",
		title: "Health",
		span: 6,
		metrics: [
			{ field: "uptime_pct", label: "Uptime", formatter: (v) => formatPercent(v, 1) },
			{ field: "alerts_per_1000km", label: "Alerts / 1000 km", formatter: (v) => numberFormatter(v, { maximumFractionDigits: 1 }) },
			{ field: "mcu_temp_avg_c", label: "MCU Temp Avg", formatter: (v) => `${numberFormatter(v, { maximumFractionDigits: 1 })} °C` },
			{ field: "mcu_temp_max_c", label: "MCU Temp Max", formatter: (v) => `${numberFormatter(v, { maximumFractionDigits: 1 })} °C` },
			{ field: "battery_temp_avg_c", label: "Battery Temp Avg", formatter: (v) => `${numberFormatter(v, { maximumFractionDigits: 1 })} °C` },
			{ field: "battery_temp_max_c", label: "Battery Temp Max", formatter: (v) => `${numberFormatter(v, { maximumFractionDigits: 1 })} °C` },
			{ field: "bms_alerts_per_100km", label: "BMS Alerts / 100 km", formatter: (v) => numberFormatter(v, { maximumFractionDigits: 1 }) },
			{ field: "mcu_alerts_per_100km", label: "MCU Alerts / 100 km", formatter: (v) => numberFormatter(v, { maximumFractionDigits: 1 }) },
			{ field: "avg_fault_recovery_time_hr", label: "Fault Recovery", formatter: (v) => formatHours(v, 2) },
		],
	},
	{
		key: "data",
		title: "Data Quality",
		span: 6,
		metrics: [
			{ field: "completeness_pct", label: "Completeness", formatter: (v) => formatPercent(v, 1) },
			{ field: "avg_data_interval_sec", label: "Avg Interval", formatter: (v) => formatSeconds(v, 1) },
		],
	},
];

const METRIC_PALETTE = [
	"#6366F1",
	"#22C55E",
	"#EF4444",
	"#F97316",
	"#0EA5E9",
	"#9333EA",
	"#FACC15",
	"#14B8A6",
	"#8B5CF6",
	"#EC4899",
	"#94A3B8",
	"#0D9488",
	"#D97706",
	"#4ADE80",
	"#38BDF8",
	"#FB7185",
	"#A855F7",
];

const HISTORY_METRIC_DEFS = [
	{ category: "Scores", path: "scores.composite_vehicle_score", label: "Composite Score", type: "percent", digits: 1 },
	{ category: "Scores", path: "scores.energy_score", label: "Energy Score", type: "percent", digits: 1 },
	{ category: "Scores", path: "scores.health_score", label: "Health Score", type: "percent", digits: 1 },
	{ category: "Scores", path: "scores.performance_score", label: "Performance Score", type: "percent", digits: 1 },
	{ category: "Utilization", path: "utilization.distance_km", label: "Distance", type: "number", digits: 1, unit: "km" },
	{ category: "Utilization", path: "utilization.trips_count", label: "Trips", type: "number", digits: 0 },
	{ category: "Utilization", path: "utilization.avg_trip_length_km", label: "Avg Trip Length", type: "number", digits: 1, unit: "km" },
	{ category: "Utilization", path: "utilization.avg_daily_km", label: "Avg Daily Distance", type: "number", digits: 1, unit: "km" },
	{ category: "Utilization", path: "utilization.driving_hours", label: "Driving Time", type: "number", digits: 1, unit: "hr" },
	{ category: "Utilization", path: "utilization.idle_hours", label: "Idle Time", type: "number", digits: 1, unit: "hr" },
	{ category: "Utilization", path: "utilization.active_days", label: "Active Days", type: "number", digits: 0, unit: "days" },
	{ category: "Energy", path: "energy.kwh_used", label: "Energy Used", type: "number", digits: 1, unit: "kWh" },
	{ category: "Energy", path: "energy.kwh_charged", label: "Energy Charged", type: "number", digits: 1, unit: "kWh" },
	{ category: "Energy", path: "energy.charging_hours", label: "Charging Hours", type: "number", digits: 1, unit: "hr" },
	{ category: "Energy", path: "energy.km_per_kwh", label: "Efficiency", type: "number", digits: 2, unit: "km/kWh" },
	{ category: "Energy", path: "energy.charging_efficiency_pct", label: "Charging Efficiency", type: "percent", digits: 1 },
	{ category: "Energy", path: "energy.estimated_energy_loss_pct", label: "Energy Loss", type: "percent", digits: 1 },
	{ category: "Energy", path: "energy.battery_soh_pct", label: "Battery SOH", type: "percent", digits: 0 },
	{ category: "Health", path: "health.uptime_pct", label: "Uptime", type: "percent", digits: 1 },
	{ category: "Health", path: "health.alerts_per_1000km", label: "Alerts / 1000 km", type: "number", digits: 1 },
	{ category: "Health", path: "health.mcu_temp_avg_c", label: "MCU Temp Avg", type: "number", digits: 1, unit: "°C" },
	{ category: "Health", path: "health.mcu_temp_max_c", label: "MCU Temp Max", type: "number", digits: 1, unit: "°C" },
	{ category: "Health", path: "health.battery_temp_avg_c", label: "Battery Temp Avg", type: "number", digits: 1, unit: "°C" },
	{ category: "Health", path: "health.battery_temp_max_c", label: "Battery Temp Max", type: "number", digits: 1, unit: "°C" },
	{ category: "Health", path: "health.bms_alerts_per_100km", label: "BMS Alerts / 100 km", type: "number", digits: 1 },
	{ category: "Health", path: "health.mcu_alerts_per_100km", label: "MCU Alerts / 100 km", type: "number", digits: 1 },
	{ category: "Health", path: "health.avg_fault_recovery_time_hr", label: "Fault Recovery", type: "number", digits: 2, unit: "hr" },
	{ category: "Data Quality", path: "data.completeness_pct", label: "Completeness", type: "percent", digits: 1 },
	{ category: "Data Quality", path: "data.avg_data_interval_sec", label: "Avg Interval", type: "number", digits: 1, unit: "s" },
];

const createMetricGetter = (path) => {
	const keys = path.split(".");
	return (row) => {
		let current = row?.metrics;
		for (const key of keys) {
			if (current == null) return null;
			current = current[key];
		}
		return current;
	};
};

const HISTORY_SERIES_DEFS = HISTORY_METRIC_DEFS.map((def, index) => ({
	...def,
	key: def.key || def.path,
	color: def.color || METRIC_PALETTE[index % METRIC_PALETTE.length],
	getValue: createMetricGetter(def.path),
}));

const coerceNumber = (value) => {
	if (value == null) return null;
	const num = Number(value);
	return Number.isFinite(num) ? num : null;
};

const formatHistoryDelta = (value, def) => {
	if (value == null || Number.isNaN(value)) return "—";
	if (value === 0) return "No change";
	const abs = Math.abs(value);
	const sign = value > 0 ? "+" : "−";
	if (def.type === "percent") {
		const digits = def.digits ?? 1;
		const formatted = numberFormatter(abs, {
			maximumFractionDigits: digits,
			minimumFractionDigits: Math.min(1, digits),
		});
		return `${sign}${formatted} pts`;
	}
	const formatted = numberFormatter(abs, {
		maximumFractionDigits: def.digits ?? 1,
	});
	return `${sign}${formatted}${def.unit ? ` ${def.unit}` : ""}`;
};

const formatHistoryValue = (value, def) => {
	if (value == null || Number.isNaN(value)) return "—";
	if (def.type === "percent") return formatPercent(value, def.digits ?? 1);
	const formatted = numberFormatter(value, {
		maximumFractionDigits: def.digits ?? 1,
	});
	return def.unit ? `${formatted} ${def.unit}` : formatted;
};

const formatHistoryTick = (value, def) => {
	if (value == null || Number.isNaN(value)) return "";
	if (def.type === "percent") {
		const digits = def.tickDigits ?? def.digits ?? 0;
		return formatPercent(value, digits);
	}
	const formatted = numberFormatter(value, {
		maximumFractionDigits: def.tickDigits ?? 0,
	});
	return def.unit ? `${formatted} ${def.unit}` : formatted;
};

const parseYearWeek = (value) => {
	if (!value) return null;
	const str = typeof value === "string" ? value : String(value);
	const match = str.match(/^(\d{4})-W(\d{1,2})$/);
	if (!match) return null;
	const [, year, rawWeek] = match;
	const week = rawWeek.padStart(2, "0");
	return { year, week };
};

const formatShortWeekLabel = (value, index) => {
	const parsed = parseYearWeek(value);
	if (parsed) return `W${parsed.week}`;
	if (value) return value;
	return `#${index + 1}`;
};

const formatFullWeekLabel = (value, index) => {
	const parsed = parseYearWeek(value);
	if (parsed) return `Week ${parsed.week} • ${parsed.year}`;
	if (value) return value;
	return `Week ${index + 1}`;
};

const VehicleInsights = ({ vehicleId, chassisNum }) => {
	const [latest, setLatest] = useState(null);
	const [history, setHistory] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);
	const [selectedSeriesKey, setSelectedSeriesKey] = useState(HISTORY_SERIES_DEFS[0].key);

	const theme = useTheme();
	const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));

	const historyCharts = useMemo(() => {
		return HISTORY_SERIES_DEFS.map((def) => {
			const data = (history || [])
				.map((row, index) => {
					const periodStart = row.period?.start ? dayjs(row.period.start) : null;
					const timestamp = periodStart?.isValid() ? periodStart.valueOf() : index;
					const value = coerceNumber(def.getValue(row));
					const weekRaw = row.year_week || "";
					return {
						timestamp,
						value,
						weekRaw,
						shortWeek: formatShortWeekLabel(weekRaw, index),
						fullWeek: formatFullWeekLabel(weekRaw, index),
						periodLabel: formatPeriod(row.period),
					};
				})
				.sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
			const points = data.filter((point) => point.value != null);
			const latestPoint = points.length ? points[points.length - 1] : null;
			const previousPoint = points.length > 1 ? points[points.length - 2] : null;
			return {
				...def,
				data,
				hasData: points.length > 0,
				latestPoint,
				previousPoint,
			};
		});
	}, [history]);

	useEffect(() => {
		if (!historyCharts.length) return;
		const current = historyCharts.find((chart) => chart.key === selectedSeriesKey);
		if (!current) {
			setSelectedSeriesKey(historyCharts[0].key);
			return;
		}
		if (!current.hasData) {
			const firstWithData = historyCharts.find((chart) => chart.hasData);
			if (firstWithData && firstWithData.key !== selectedSeriesKey) {
				setSelectedSeriesKey(firstWithData.key);
			}
		}
	}, [historyCharts, selectedSeriesKey]);

	const selectedSeries =
		historyCharts.find((chart) => chart.key === selectedSeriesKey) ||
		historyCharts.find((chart) => chart.hasData) ||
		historyCharts[0] ||
		null;

	const selectedKey = selectedSeries?.key || selectedSeriesKey || HISTORY_SERIES_DEFS[0].key;

	const groupedCharts = useMemo(() => {
		const map = new Map();
		historyCharts.forEach((chart) => {
			const key = chart.category || "Other";
			if (!map.has(key)) map.set(key, []);
			map.get(key).push(chart);
		});
		return Array.from(map.entries()).map(([category, items]) => ({ category, items }));
	}, [historyCharts]);

	useEffect(() => {
		if (!vehicleId) return;
		const controller = new AbortController();
		const load = async () => {
			setLoading(true);
			setError(null);
			setLatest(null);
			setHistory([]);
			const latestUrl = new URL(
				`analytics/vehicles/${vehicleId}/insights/latest/`,
				BACKEND_URL
			).toString();
			const historyUrl = new URL(
				`analytics/vehicles/${vehicleId}/insights/history/`,
				BACKEND_URL
			).toString();
			const headers = {
				Accept: "application/json",
				Authorization: `Bearer ${BACKEND_TOKEN}`,
			};
			try {
				const latestResp = await fetch(latestUrl, {
					headers,
					signal: controller.signal,
				});
				if (!latestResp.ok) {
					throw new Error(`Latest insights: HTTP ${latestResp.status}`);
				}
				const latestJson = await latestResp.json();
				if (!latestJson.success) {
					throw new Error(latestJson.message || "Failed to load latest insights");
				}
				setLatest(latestJson.data || null);
			} catch (err) {
				if (controller.signal.aborted) return;
				setError(err.message || "Unable to load vehicle insights");
				setLoading(false);
				return;
			}
			try {
				const historyResp = await fetch(historyUrl, {
					headers,
					signal: controller.signal,
				});
				if (!historyResp.ok) {
					throw new Error(`Insights history: HTTP ${historyResp.status}`);
				}
				const historyJson = await historyResp.json();
				if (!historyJson.success) {
					throw new Error(historyJson.message || "Failed to load history");
				}
				setHistory(historyJson.data?.results || []);
			} catch (err) {
				if (controller.signal.aborted) return;
				setHistory([]);
				setError(err.message || "Unable to load historical insights");
			} finally {
				if (!controller.signal.aborted) setLoading(false);
			}
		};
		load();
		return () => controller.abort();
	}, [vehicleId]);

	if (!vehicleId) {
		return <Typography variant="body2">Select a vehicle to view insights.</Typography>;
	}

	if (loading && !latest) {
		return (
			<Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 320 }}>
				<CircularProgress size={28} />
			</Box>
		);
	}

	if (error && !latest) {
		return (
			<Box sx={{ p: 2 }}>
				<Typography color="error" variant="body2">
					{error}
				</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
			{latest && (
				<Card>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							Weekly Insights
						</Typography>
						<Typography variant="body2" color="text.secondary">
							{chassisNum ? `${chassisNum} • ` : ""}
							Week {latest.year_week} • {formatPeriod(latest.period)}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							Computed at {formatTimestamp(latest.computed_at)} (v{latest.version})
						</Typography>
					</CardContent>
					<Divider />
					<Box sx={{ p: 2 }}>
						<Grid container spacing={2}>
							{SECTION_DEFS.map((section) => {
								const sectionMetrics = latest.metrics?.[section.key] || {};
								return (
									<Grid item xs={12} md={section.span} key={section.key}>
										<Paper
											square={false}
											variant="outlined"
											sx={{ p: 2, height: "100%" }}
										>
											<Typography variant="subtitle2" color="text.secondary" gutterBottom>
												{section.title}
											</Typography>
											<Box sx={{ display: "grid", rowGap: 1.25 }}>
												{section.metrics.map((metric) => (
													<Box
														key={metric.field}
														sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 2 }}
													>
														<Typography variant="body2" color="text.secondary">
															{metric.label}
														</Typography>
														<Typography variant="body2" fontWeight={600}>
															{metric.formatter(sectionMetrics[metric.field])}
														</Typography>
													</Box>
												))}
											</Box>
										</Paper>
									</Grid>
								);
							})}
						</Grid>
					</Box>
				</Card>
			)}
			<Card>
				<CardContent>
					<Typography variant="h6" gutterBottom>
						Historical Trends
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Most recent {numberFormatter(history?.length || 0, { maximumFractionDigits: 0 })} weeks
					</Typography>
				</CardContent>
				<Divider />
				{error && latest ? (
					<Box sx={{ p: 2 }}>
						<Typography color="error" variant="body2">
							{error}
						</Typography>
					</Box>
				) : history && history.length > 0 ? (
					<Box sx={{ p: 2, display: "flex", flexDirection: "column", gap: 3 }}>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
							<Typography variant="subtitle2" color="text.secondary">
								Metric
							</Typography>
							{isSmallScreen ? (
								<TextField
									select
									label="Metric"
									size="small"
									value={selectedKey}
									onChange={(event) => setSelectedSeriesKey(event.target.value)}
									SelectProps={{
										MenuProps: {
											PaperProps: { sx: { maxHeight: 360 } },
										},
									}}
									sx={{ minWidth: 220 }}
								>
									{groupedCharts.map((group) => (
										<React.Fragment key={group.category}>
											<ListSubheader>{group.category}</ListSubheader>
											{group.items.map((chart) => (
												<MenuItem key={chart.key} value={chart.key}>
													{chart.label}
													{chart.hasData ? "" : " (no data)"}
												</MenuItem>
											))}
										</React.Fragment>
									))}
								</TextField>
							) : (
								<Stack spacing={1.25}>
									{groupedCharts.map((group) => (
										<Stack
											key={group.category}
											direction="row"
											alignItems="center"
											spacing={1}
											sx={{ flexWrap: "wrap" }}
										>
											<Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
												{group.category}
											</Typography>
											<ToggleButtonGroup
												value={selectedKey}
												exclusive
												size="small"
												onChange={(_, value) => value && setSelectedSeriesKey(value)}
												sx={{
													flexWrap: "wrap",
													gap: 1,
													'& .MuiToggleButton-root': {
														textTransform: "none",
														minWidth: 132,
													},
												}}
											>
												{group.items.map((chart) => (
													<ToggleButton
														key={chart.key}
														value={chart.key}
														sx={{ opacity: chart.hasData ? 1 : 0.6 }}
													>
														{chart.label}
														{chart.hasData ? "" : " • no data"}
													</ToggleButton>
												))}
											</ToggleButtonGroup>
										</Stack>
									))}
								</Stack>
							)}
						</Box>

						{selectedSeries?.hasData ? (
							<Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
								<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
									<Typography variant="h5" fontWeight={600}>
										{selectedSeries.latestPoint
											? formatHistoryValue(selectedSeries.latestPoint.value, selectedSeries)
											: "—"}
									</Typography>
									<Typography variant="body2" color="text.secondary">
										{selectedSeries.latestPoint?.fullWeek || "Latest"}
										{selectedSeries.latestPoint?.periodLabel && selectedSeries.latestPoint?.periodLabel !== "—"
											? ` • ${selectedSeries.latestPoint.periodLabel}`
											: ""}
									</Typography>
									{selectedSeries.previousPoint?.value != null && selectedSeries.latestPoint?.value != null && (
										<Typography
											variant="body2"
											sx={{
												color:
													selectedSeries.latestPoint.value - selectedSeries.previousPoint.value > 0
														? theme.palette.success.main
														: selectedSeries.latestPoint.value - selectedSeries.previousPoint.value < 0
														? theme.palette.error.main
														: theme.palette.text.secondary,
											}}
										>
											{selectedSeries.latestPoint.value - selectedSeries.previousPoint.value > 0
												? "▲"
												: selectedSeries.latestPoint.value - selectedSeries.previousPoint.value < 0
												? "▼"
												: ""}{" "}
											{formatHistoryDelta(
												selectedSeries.latestPoint.value - selectedSeries.previousPoint.value,
												selectedSeries
											)}
											{selectedSeries.previousPoint?.fullWeek
												? ` vs ${selectedSeries.previousPoint.fullWeek}`
												: " vs previous week"}
										</Typography>
									)}
								</Box>
								<Box sx={{ width: "100%", height: { xs: 260, sm: 320 } }}>
									<ResponsiveContainer width="100%" height="100%">
										<LineChart data={selectedSeries.data} margin={{ top: 16, right: 20, left: 0, bottom: 16 }}>
											<CartesianGrid strokeDasharray="4 3" stroke={theme.palette.action.hover} />
											<XAxis
												dataKey="shortWeek"
												tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
												interval="preserveStartEnd"
												minTickGap={12}
												axisLine={{ stroke: theme.palette.divider }}
												tickLine={false}
											/>
											<YAxis
												tickFormatter={(value) => formatHistoryTick(value, selectedSeries)}
												width={selectedSeries.type === "percent" ? 76 : 68}
												axisLine={{ stroke: theme.palette.divider }}
												tickLine={false}
												tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
											/>
											<Tooltip
												formatter={(value) => [formatHistoryValue(value, selectedSeries), selectedSeries.label]}
												labelFormatter={(label, payload) => {
													if (!payload || !payload.length) return label;
													const dataPoint = payload[0].payload;
													const weekLabel = dataPoint.fullWeek || label;
													const period = dataPoint.periodLabel;
													return period && period !== "—" ? `${weekLabel} • ${period}` : weekLabel;
												}}
												contentStyle={{
													borderRadius: 8,
													borderColor: theme.palette.divider,
													boxShadow: theme.shadows[3],
												}}
											/>
											<Line
												dataKey="value"
												type="monotone"
												stroke={selectedSeries.color || theme.palette.primary.main}
												strokeWidth={2.8}
												dot={{ r: 0 }}
												activeDot={{
													r: 5,
													strokeWidth: 2,
													stroke: theme.palette.background.paper,
													fill: selectedSeries.color || theme.palette.primary.main,
												}}
												connectNulls
											/>
										</LineChart>
									</ResponsiveContainer>
								</Box>
							</Box>
						) : (
							<Box
								sx={{
									minHeight: 220,
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									borderRadius: 1,
									backgroundColor: theme.palette.action.hover,
									color: theme.palette.text.secondary,
									textAlign: "center",
									px: 2,
								}}
							>
								<Typography variant="body2">No historical data available for the selected metric.</Typography>
							</Box>
						)}
					</Box>
				) : (
					<Box sx={{ p: 2 }}>
						<Typography variant="body2" color="text.secondary">
							No historical insights available yet.
						</Typography>
					</Box>
				)}
				{loading && latest && (
					<Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1 }}>
						<CircularProgress size={18} />
						<Typography variant="body2" color="text.secondary">
							Updating insights…
						</Typography>
					</Box>
				)}
			</Card>
		</Box>
	);
};

export default VehicleInsights;